import { Button, CommentInput } from "@dash/form";
import { useRef } from "react";
import { MdOutlineSend } from "react-icons/md";

import { SupportTicketNoteAttachmentList } from "./SupportTicketNoteAttachmentList";
import { noteAttachmentAccept, readNoteAttachmentFile, type NoteAttachmentEntry } from "./supportTicketNoteAttachmentTypes";

type Mention = {
  user: string;
  tagPosition: number;
  tagLength: number;
};

type Props = {
  commentValue: string;
  mentions: Mention[];
  attachments: NoteAttachmentEntry[];
  onCommentChange: (comment: string, mentions: Mention[]) => void;
  onAttachmentsChange: (attachments: NoteAttachmentEntry[]) => void;
  onSend: () => void;
  canSend: boolean;
  isLoading: boolean;
};

export function SupportTicketNoteComposer({
  commentValue,
  mentions,
  attachments,
  onCommentChange,
  onAttachmentsChange,
  onSend,
  canSend,
  isLoading,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFilesSelected = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const results = await Promise.allSettled(Array.from(files).map(readNoteAttachmentFile));
    const entries = results.flatMap((result) => (result.status === "fulfilled" ? [result.value] : []));
    if (entries.length > 0) {
      onAttachmentsChange([...attachments, ...entries]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <CommentInput
        enableMentions={true}
        enableTags={false}
        commentValue={commentValue}
        mentionsValues={mentions}
        onChange={(comment, nextMentions) => {
          onCommentChange(comment, nextMentions ?? []);
        }}
        disableMaxLength
        onAttachClick={() => fileInputRef.current?.click()}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={noteAttachmentAccept}
        multiple
        onChange={(event) => {
          void handleFilesSelected(event.target.files);
        }}
        css={{ display: "none" }}
      />
      <SupportTicketNoteAttachmentList
        attachments={attachments}
        onRemove={(index) => {
          onAttachmentsChange(attachments.filter((_, i) => i !== index));
        }}
      />
      <div css={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          icon={<MdOutlineSend />}
          css={{ borderRadius: "50px", width: "auto", whiteSpace: "nowrap" }}
          onClick={onSend}
          isDisabled={!canSend}
          isLoading={isLoading}
        >
          Wyślij notatkę
        </Button>
      </div>
    </div>
  );
}
